import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { formatCost } from '@/utils/formatCurrency.util';

interface CostChipProps {
  /** Label rendered before the value. Empty string renders value-only
   *  (e.g. when wrapped in a `<Field>` that already owns its label). */
  label: string;
  cost?: number | null;
  /** ISO currency code from the backend. USD is the contract; a non-USD code
   *  degrades to `—` (never a misleading `$`). Defaults to `'USD'` so callers
   *  that don't yet thread the field keep today's behavior. */
  currency?: string;
  truncated?: boolean;
  isLoading: boolean;
  isError: boolean;
  className?: string;
}

/**
 * Presentational cost chip. Does NO data fetching — the parent passes
 * the hook result. States:
 *  - loading  → muted `…`
 *  - error    → `—` (never blocks the surface)
 *  - cost null (404 / unowned)  → renders nothing
 *  - non-USD currency  → `—` (USD-contract guard; never a wrong symbol)
 *  - success  → `formatCost(cost, currency)`, with a trailing `+` + tooltip
 *               when the total is truncated (partial lower bound)
 * Chariot design tokens only.
 */
export function CostChip({
  label,
  cost,
  currency = 'USD',
  truncated,
  isLoading,
  isError,
  className,
}: CostChipProps) {
  const renderLabel = label ? (
    <span className="text-chariot-text-secondary text-sm font-medium">
      {label}
    </span>
  ) : null;

  const wrap = (value: ReactNode, title?: string) => (
    <span
      className={cn('flex items-center gap-2', className)}
      title={title}
      data-testid="cost-chip"
    >
      {renderLabel}
      {value}
    </span>
  );

  if (isLoading) {
    return wrap(
      <span className="text-chariot-text-secondary text-sm tabular-nums">
        …
      </span>
    );
  }

  if (isError) {
    return wrap(
      <span className="text-chariot-text-secondary text-sm tabular-nums">
        —
      </span>,
      'Cost unavailable'
    );
  }

  // 404 / no owned cost → render nothing (chip absent).
  if (cost == null) {
    return null;
  }

  // USD-contract guard: a non-USD code degrades to `—` rather than rendering
  // a misleading `$`. USD (the contract) formats byte-identically to before.
  const formatted = formatCost(cost, currency);
  if (formatted == null) {
    return wrap(
      <span className="text-chariot-text-secondary text-sm tabular-nums">
        —
      </span>,
      'Cost unavailable'
    );
  }

  return wrap(
    <span className="text-chariot-text-primary text-sm tabular-nums">
      {formatted}
      {truncated ? '+' : ''}
    </span>,
    truncated ? 'Partial total — exceeds the query window cap' : undefined
  );
}
